import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Icon } from '../common/Icon';
import { FaWaveSquare, FaPlay, FaStop, FaPlus } from 'react-icons/fa';
import { WhiteNoiseGeneratorNode, NoiseType, NoiseSettings, DEFAULT_FREQUENCY_BANDS, FrequencyBand, PRESET_EQ_CURVES } from '../../utils/audio/WhiteNoiseGeneratorNode';

const GeneratorContainer = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #333;
`;

const GeneratorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const GeneratorTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
`;

const PreviewButton = styled.button<{ $isPlaying?: boolean }>`
  background: ${props => props.$isPlaying ? '#f44336' : '#4caf50'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$isPlaying ? '#d32f2f' : '#45a049'};
  }
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 24px;
`;

const PresetButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? '#00bcd4' : '#2a2a2a'};
  color: white;
  border: 1px solid ${props => props.$active ? '#00bcd4' : '#444'};
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    background: ${props => props.$active ? '#00acc1' : '#333'};
    border-color: ${props => props.$active ? '#00acc1' : '#555'};
  }

  small {
    display: block;
    font-size: 10px;
    opacity: 0.7;
    margin-top: 2px;
  }
`;

const ControlsSection = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const FrequencyBandsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 16px;
  background: #222;
  border-radius: 6px;
`;

const FrequencyBandControl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 40px;
`;

const BandValue = styled.span`
  color: #00bcd4;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  min-height: 16px;
`;

const VerticalSlider = styled.input<{ $isLive?: boolean }>`
  writing-mode: bt-lr; /* IE */
  writing-mode: vertical-lr; 
  direction: rtl;
  appearance: slider-vertical;
  width: 20px;
  height: 150px;
  background: transparent;
  outline: none;
  cursor: pointer;
  
  &::-webkit-slider-track {
    width: 6px;
    height: 100%;
    background: ${props => props.$isLive ? 'linear-gradient(180deg, #444, #555, #444)' : '#444'};
    background-size: ${props => props.$isLive ? '20px 20px' : 'auto'};
    animation: ${props => props.$isLive ? 'shimmer-vertical 2s linear infinite' : 'none'};
    border-radius: 3px;
  }
  
  @keyframes shimmer-vertical {
    0% { background-position: 0 0; }
    100% { background-position: 0 20px; }
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.$isLive ? '#00e5ff' : '#00bcd4'};
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: ${props => props.$isLive ? '0 0 8px rgba(0, 229, 255, 0.5)' : 'none'};
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  &::-moz-range-track {
    width: 6px;
    height: 100%;
    background: ${props => props.$isLive ? 'linear-gradient(180deg, #444, #555, #444)' : '#444'};
    background-size: ${props => props.$isLive ? '20px 20px' : 'auto'};
    animation: ${props => props.$isLive ? 'shimmer-vertical 2s linear infinite' : 'none'};
    border-radius: 3px;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.$isLive ? '#00e5ff' : '#00bcd4'};
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    box-shadow: ${props => props.$isLive ? '0 0 8px rgba(0, 229, 255, 0.5)' : 'none'};
  }

  &::-moz-range-thumb:hover {
    transform: scale(1.2);
  }
`;

const ControlGroup = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ControlLabel = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const ControlValue = styled.span`
  color: #00bcd4;
  font-size: 12px;
  font-weight: 600;
`;

const Slider = styled.input<{ $isLive?: boolean }>`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${props => props.$isLive ? 'linear-gradient(45deg, #444, #555, #444)' : '#444'};
  background-size: ${props => props.$isLive ? '20px 20px' : 'auto'};
  animation: ${props => props.$isLive ? 'shimmer 2s linear infinite' : 'none'};
  outline: none;
  -webkit-appearance: none;
  
  @keyframes shimmer {
    0% { background-position: 0 0; }
    100% { background-position: 20px 0; }
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.$isLive ? '#00e5ff' : '#00bcd4'};
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: ${props => props.$isLive ? '0 0 8px rgba(0, 229, 255, 0.5)' : 'none'};
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.$isLive ? '#00e5ff' : '#00bcd4'};
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    box-shadow: ${props => props.$isLive ? '0 0 8px rgba(0, 229, 255, 0.5)' : 'none'};
  }

  &::-moz-range-thumb:hover {
    transform: scale(1.2);
  }
`;

const DurationInput = styled.input`
  width: 80px;
  padding: 6px 8px;
  border: 1px solid #444;
  border-radius: 4px;
  background: #1a1a1a;
  color: #ffffff;
  font-size: 12px;
  text-align: right;
  
  &:focus {
    outline: none;
    border-color: #00bcd4;
  }
`;

const AddTrackButton = styled.button`
  width: 100%;
  background: #00bcd4;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: #00acc1;
  }
`;

interface NoisePreset {
  name: string;
  type: NoiseType;
  description: string;
  frequencyBands?: Partial<FrequencyBand>[];
}

const PRESETS: NoisePreset[] = [
  { 
    name: 'Brown Noise', 
    type: 'brown', 
    description: 'Deep EQ (-6dB/octave)' 
  },
  { 
    name: 'Pink Noise', 
    type: 'pink', 
    description: 'Natural EQ (-3dB/octave)' 
  },
  { 
    name: 'White Noise', 
    type: 'white', 
    description: 'Flat EQ (all frequencies equal)' 
  }


];

// Add a special preset for custom
const CUSTOM_PRESET: NoisePreset = {
  name: 'Custom',
  type: 'white', // Base type doesn't matter for custom
  description: 'User-defined EQ curve'
};

interface WhiteNoiseGeneratorProps {
  audioContext: AudioContext | null;
  onAddTrack: (settings: NoiseSettings, presetName: string) => void;
  timelineDuration?: number;
}

export const WhiteNoiseGenerator: React.FC<WhiteNoiseGeneratorProps> = ({
  audioContext,
  onAddTrack,
  timelineDuration
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('White Noise');
  const [frequencyBands, setFrequencyBands] = useState<FrequencyBand[]>([...PRESET_EQ_CURVES.white]);
  const [volume, setVolume] = useState<number>(0.3);
  const [duration, setDuration] = useState<number>(timelineDuration || 300);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState<boolean>(false);
  const [isManuallyEdited, setIsManuallyEdited] = useState<boolean>(false);
  
  const previewGeneratorRef = useRef<WhiteNoiseGeneratorNode | null>(null);
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPreview();
    };
  }, []);

  // Update duration when timeline duration changes
  useEffect(() => {
    if (!isManuallyEdited && timelineDuration !== undefined && timelineDuration > 0) {
      setDuration(Math.ceil(timelineDuration));
    } else if (!isManuallyEdited && (!timelineDuration || timelineDuration === 0)) {
      setDuration(300);
    }
  }, [timelineDuration, isManuallyEdited]);

  const stopPreview = useCallback(() => {
    if (previewGeneratorRef.current) {
      previewGeneratorRef.current.stop();
      previewGeneratorRef.current.disconnect();
      previewGeneratorRef.current = null;
    }

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }

    setIsPreviewPlaying(false);
  }, []);

  const handlePresetSelect = useCallback((preset: NoisePreset) => {
    setSelectedPreset(preset.name);
    
    // Get the correct preset curve - deep copy to avoid mutations
    const presetCurve = PRESET_EQ_CURVES[preset.type].map(band => ({ ...band }));
    setFrequencyBands(presetCurve);
    
    // Update the live preview if it's playing
    if (isPreviewPlaying && previewGeneratorRef.current) {
      // Update all bands to match the preset
      previewGeneratorRef.current.updateAllBands(presetCurve);
    }
  }, [isPreviewPlaying]);

  const startPreview = useCallback(() => {
    if (!audioContext || isPreviewPlaying) return;

    const settings: NoiseSettings = {
      noiseType: 'white', // Always use white noise as base
      frequencyBands,
      volume,
      duration: 10 // 10 second preview
    };

    const generator = new WhiteNoiseGeneratorNode(audioContext, settings);
    generator.connect(audioContext.destination);
    
    // Start is now async
    generator.start().then(() => {
      previewGeneratorRef.current = generator;
      setIsPreviewPlaying(true);
    }).catch((error) => {
      console.error('Failed to start noise generator:', error);
    });

    // Auto-stop after 10 seconds
    previewTimeoutRef.current = setTimeout(() => {
      stopPreview();
    }, 10000);
  }, [audioContext, isPreviewPlaying, frequencyBands, volume, stopPreview]);

  const handleBandGainChange = useCallback((bandIndex: number, gain: number) => {
    const newBands = [...frequencyBands];
    newBands[bandIndex] = { ...newBands[bandIndex], gain };
    setFrequencyBands(newBands);
    
    // Automatically select Custom preset when user modifies sliders
    setSelectedPreset('Custom');
    
    // Update in real-time if preview is playing
    if (isPreviewPlaying && previewGeneratorRef.current) {
      previewGeneratorRef.current.updateBandGain(bandIndex, gain);
    }
  }, [frequencyBands, isPreviewPlaying]);

  const handleVolumeChange = useCallback((value: number) => {
    setVolume(value);
    
    // Update volume in real-time
    if (isPreviewPlaying && previewGeneratorRef.current) {
      previewGeneratorRef.current.setVolume(value);
    }
  }, [isPreviewPlaying]);

  const handleAddTrack = useCallback(() => {
    const settings: NoiseSettings = {
      noiseType: 'white', // Always use white noise as base
      frequencyBands,
      volume,
      duration
    };

    // Determine the preset name to use
    const presetName = selectedPreset === 'Custom' ? 'Custom Noise' : `${selectedPreset}`;

    onAddTrack(settings, presetName);
    
    // Stop preview if playing
    if (isPreviewPlaying) {
      stopPreview();
    }
  }, [frequencyBands, volume, duration, selectedPreset, onAddTrack, isPreviewPlaying, stopPreview]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <GeneratorContainer>
      <GeneratorHeader>
        <GeneratorTitle>
          <Icon icon={FaWaveSquare} />
          White Noise Generator
        </GeneratorTitle>
        <PreviewButton 
          $isPlaying={isPreviewPlaying}
          onClick={isPreviewPlaying ? stopPreview : startPreview}
          disabled={!audioContext}
        >
          <Icon icon={isPreviewPlaying ? FaStop : FaPlay} />
          {isPreviewPlaying ? 'Stop Preview' : 'Live Preview'}
        </PreviewButton>
      </GeneratorHeader>

      <PresetGrid>
        {PRESETS.map(preset => (
          <PresetButton
            key={preset.name}
            $active={selectedPreset === preset.name}
            onClick={() => handlePresetSelect(preset)}
          >
            {preset.name}
            <small>{preset.description}</small>
          </PresetButton>
        ))}
        <PresetButton
          $active={selectedPreset === 'Custom'}
          onClick={() => setSelectedPreset('Custom')}
        >
          Custom
          <small>User-defined EQ</small>
        </PresetButton>
      </PresetGrid>

      <ControlsSection>
        <FrequencyBandsContainer>
          {frequencyBands.map((band, index) => (
            <FrequencyBandControl key={band.name}>
              <VerticalSlider
                type="range"
                min="-36"
                max="12"
                step="0.5"
                value={band.gain}
                $isLive={isPreviewPlaying}
                onChange={(e) => handleBandGainChange(index, Number(e.target.value))}
                title={`${band.name} (${band.minFreq < 1000 ? band.minFreq : `${(band.minFreq / 1000).toFixed(0)}k`}-${band.maxFreq < 1000 ? band.maxFreq : `${(band.maxFreq / 1000).toFixed(0)}k`}Hz)`}
              />
              <BandValue>{band.gain > 0 ? '+' : ''}{band.gain.toFixed(1)}dB</BandValue>
            </FrequencyBandControl>
          ))}
        </FrequencyBandsContainer>

        <ControlGroup>
          <ControlLabel>
            Master Volume
            <ControlValue>{Math.round(volume * 100)}%</ControlValue>
          </ControlLabel>
          <Slider
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            $isLive={isPreviewPlaying}
            onChange={(e) => handleVolumeChange(Number(e.target.value))}
          />
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>
            Track Duration
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DurationInput
                type="number"
                min="30"
                max="3600"
                value={duration}
                onChange={(e) => {
                  setDuration(Number(e.target.value));
                  setIsManuallyEdited(true);
                }}
                title="Track duration in seconds"
              />
              {timelineDuration && timelineDuration > 0 && isManuallyEdited && (
                <button
                  type="button"
                  onClick={() => {
                    setDuration(Math.ceil(timelineDuration));
                    setIsManuallyEdited(false);
                  }}
                  style={{
                    background: '#333',
                    color: '#fff',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    cursor: 'pointer'
                  }}
                  title="Reset to timeline duration"
                >
                  Reset
                </button>
              )}
              <span style={{ color: '#ccc' }}>seconds</span>
            </div>
          </ControlLabel>
          <small style={{ color: '#999', fontSize: '11px' }}>
            {formatDuration(duration)} 
            {isManuallyEdited ? ' (custom)' : timelineDuration && timelineDuration > 0 ? ' (matches timeline)' : ' (default)'}
          </small>
        </ControlGroup>
      </ControlsSection>

      <AddTrackButton onClick={handleAddTrack} disabled={!audioContext}>
        <Icon icon={FaPlus} />
        Add Noise Track to Timeline
      </AddTrackButton>
    </GeneratorContainer>
  );
};

export default WhiteNoiseGenerator;